import { useId, type ReactNode } from "react";

/**
 * The panel a section sits in — `StatCard`'s surface, for content that is not a row of figures.
 *
 * ==========================================================================================
 * WHY THIS EXISTS
 * ==========================================================================================
 *
 * Five of the dashboard's seven pieces get their panel from `StatCard`. The two charts did not: they
 * were bare `<section>`s with an uppercase heading and nothing around them, so the page read as five
 * cards with two unframed strips between and below them.
 *
 * IT WAS `ChartCard` IN THE DASHBOARD FOLDER UNTIL 2026-08-14, and moved up a level when the savings
 * page turned out to have the same two problems — an unframed chart and an unframed account list. Three
 * pages' worth of consumers is the point at which a surface stops belonging to one of them.
 *
 * The cost was not only visual. The net worth chart's heading was "Last 12 months" — which names the
 * window and not the subject — and with no panel to bound it, it did not read as a section of its own
 * at all. It was reported as a missing feature by somebody looking straight at it: the request was to
 * "add a net worth graph section", for a four-layer net worth graph that had been there since US1.
 *
 * ==========================================================================================
 * WHY IT IS NOT A `StatCard` WITH A CHART INSIDE
 * ==========================================================================================
 *
 * `StatCard`'s own documentation draws the line: "everything in a stat card must be a number or a
 * name, because the moment one starts holding a sentence the row stops being scannable". A chart is
 * neither, and adding a `children` slot to the component that guarantees the KPI row's rhythm is how
 * that guarantee stops holding.
 *
 * It is also not in `@gladis/ui`. The classes are copied from `StatCard` rather than shared, which is
 * a real duplication — but promoting a generic `Panel` is a design-system decision, and this feature
 * has already made two of those (the waterfall and the ranked bar) that genuinely needed making.
 * `colour-tokens-exist.test.ts` scans `apps/app/src`, so the tokens here are checked either way.
 *
 * `cn` is not reached for because `@gladis/ui` does not export it and nothing in `apps/app` composes
 * classes conditionally. One optional suffix does not warrant being the first place that does.
 */
export function SectionCard({
  title,
  value,
  hint,
  controls,
  children,
  className,
  testId,
  valueTestId,
}: {
  /**
   * Names the SUBJECT, not the window — "Net worth over time", not "Last 12 months".
   *
   * OPTIONAL, for a section whose content already carries its own heading. `AccountList` renders
   * `<section aria-labelledby="savings-accounts-heading">` with an `h2` inside it, so a title here would
   * be a second heading naming the same region — and the region is already named, which is what the
   * accessibility rule actually asks for.
   */
  title?: string;
  /**
   * A headline figure above the chart, already formatted.
   *
   * For a section whose chart is a DECOMPOSITION of one number: the expense summary's total is the
   * figure and the ranking is what it is made of. Omitted where the chart has no single total — the net
   * worth chart's own total is drawn as a line ON the plot, which is better than printing it twice.
   */
  value?: string;
  /** One line under the heading: the window, the scope, what the figures are measured on. */
  hint?: ReactNode;
  /** Controls that change what the chart shows, at the end of the heading row — `StatCard`'s slot. */
  controls?: ReactNode;
  children: ReactNode;
  className?: string;
  testId?: string;
  /**
   * For the e2e suite, on the FIGURE ALONE — `StatCard`'s distinction, and it exists for the same
   * reason: `toHaveText("€ 6.400,00")` wants an element whose entire text is the figure.
   */
  valueTestId?: string;
}): ReactNode {
  const headingId = useId();

  return (
    <section
      {...(title === undefined ? {} : { "aria-labelledby": headingId })}
      {...(testId === undefined ? {} : { "data-testid": testId })}
      className={`rounded-control border border-line bg-panel p-5${className === undefined ? "" : ` ${className}`}`}
    >
      {/*
        `h2` and the same eyebrow type as `StatCard`'s label, so a screen reader's outline reads the
        seven pieces as seven peers — and so a chart section and a figure section are visibly the
        same KIND of thing on the page, which is the whole point of framing them alike.
      */}
      {title === undefined && controls === undefined ? null : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          {title === undefined ? null : (
            <h2 id={headingId} className="text-xs font-medium tracking-wide text-muted uppercase">
              {title}
            </h2>
          )}
          {controls === undefined ? null : (
            <div className="flex items-center gap-2">{controls}</div>
          )}
        </div>
      )}

      {/* The same type scale as `StatCard`'s headline, so a chart section with a figure and a stat card
          are the same KIND of thing at a glance — which is the point of framing them alike. */}
      {value === undefined ? null : (
        <p className="mt-2 text-2xl font-semibold text-ink">
          <span {...(valueTestId === undefined ? {} : { "data-testid": valueTestId })}>
            {value}
          </span>
        </p>
      )}

      {hint === undefined ? null : <p className="mt-1 text-xs text-muted">{hint}</p>}

      {/* A MINIMUM HEIGHT rather than a minimum width (FR-087): the chart must narrow to a phone,
          and what it must not do is collapse to a strip too short to read a trend from. */}
      <div className={title === undefined ? "min-h-64" : "mt-4 min-h-64"}>{children}</div>
    </section>
  );
}
